import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { BotModeSection } from '../components/tenant-sections/BotModeSection.tsx';
import { ChannelsSection } from '../components/tenant-sections/ChannelsSection.tsx';
import { ErpSection } from '../components/tenant-sections/ErpSection.tsx';
import { IdentitySection } from '../components/tenant-sections/IdentitySection.tsx';
import { KnowledgeSection } from '../components/tenant-sections/KnowledgeSection.tsx';
import { SectionTabs } from '../components/tenant-sections/SectionNav.tsx';
import { TenantStatusControl } from '../components/tenant-sections/TenantStatusControl.tsx';
import {
  type EditSectionId,
  channelsValueFromStatus,
  editNavItems,
} from '../components/tenant-sections/status.ts';
import { Button } from '../components/ui/index.ts';
import { useAsync } from '../hooks/useAsync.ts';
import { api } from '../lib/api.ts';

/**
 * Edición de un tenant.
 *
 * Monta las MISMAS secciones que el wizard, pero en tabs y en modo 'edit':
 * cada una se guarda sola contra su endpoint. Antes esta pantalla era de solo
 * lectura y cualquier corrección había que hacerla por CLI.
 */
export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [active, setActive] = useState<EditSectionId>('identidad');

  const id = tenantId ?? '';
  const { data: detail, status, error, refetch } = useAsync(() => api.tenants.get(id));
  const tenant = detail?.tenant;
  // Config guardada del tenant. Sin esto las secciones arrancarían con
  // defaults y al guardar reemplazarían la configuración real.
  const cfg = (detail?.config ?? {}) as {
    erp?: { type?: string } & Record<string, unknown>;
    bot?: { mode?: string };
  };
  const { data: onboarding, refetch: refetchOnboarding } = useAsync(() =>
    api.tenants.getOnboardingStatus(id),
  );

  function onSectionSaved() {
    refetchOnboarding();
  }

  /**
   * Las tabs suben a la banda del shell, pero SIGUEN siendo las de estado.
   *
   * No se migran a `RouteTabs` a propósito: éstas muestran la completitud de
   * cada sección (✓ / pendiente), que es lo que guía el onboarding de un
   * tenant nuevo. `RouteTabs` no modela eso, y cambiarlas sólo para unificar
   * el componente perdería la única señal de qué falta configurar.
   */
  const tabs =
    status === 'success' && tenant ? (
      <div className="shrink-0 border-b border-edge-subtle bg-surface-base px-6 py-2">
        <SectionTabs
          items={editNavItems(onboarding)}
          activeId={active}
          onSelect={(sectionId) => setActive(sectionId as EditSectionId)}
        />
      </div>
    ) : undefined;

  return (
    <AppShell sectionTabs={tabs}>
      <div className="flex flex-col h-full animate-fade-in">
        <PageHeader
          title={tenant?.name ?? id ?? '—'}
          subtitle={tenant ? `Identificador: ${tenant.tenantId}` : undefined}
          actions={
            <>
              {tenant && <StatusBadge status={tenant.status} />}
              <Button variant="outline" size="sm" onClick={() => navigate('/tenants')}>
                ← Lista
              </Button>
            </>
          }
        />

        <div className="flex-1 overflow-auto p-6 space-y-6 bg-surface-sunken">
          {status === 'loading' && <Skeleton rows={4} />}
          {status === 'error' && error && <ErrorAlert message={error} />}

          {status === 'success' && tenant && (
            <>
              <TenantStatusControl
                tenantId={tenant.tenantId}
                current={tenant.status}
                onChanged={refetch}
              />

              {active === 'identidad' && (
                <IdentitySection
                  mode="edit"
                  testId="tenant-section-identidad"
                  tenantId={tenant.tenantId}
                  value={{ tenantId: tenant.tenantId, name: tenant.name }}
                />
              )}

              {active === 'canales' && (
                <ChannelsSection
                  mode="edit"
                  testId="tenant-section-canales"
                  tenantId={tenant.tenantId}
                  value={channelsValueFromStatus(onboarding)}
                  onSaved={onSectionSaved}
                />
              )}

              {active === 'integracion' && (
                <ErpSection
                  mode="edit"
                  testId="tenant-section-integracion"
                  tenantId={tenant.tenantId}
                  value={
                    cfg.erp
                      ? {
                          type: cfg.erp.type,
                          config: cfg.erp as Record<string, never>,
                        }
                      : {}
                  }
                  onSaved={onSectionSaved}
                />
              )}

              {active === 'conocimiento' && (
                <KnowledgeSection
                  mode="edit"
                  testId="tenant-section-conocimiento"
                  tenantId={tenant.tenantId}
                  value={undefined}
                  onSaved={onSectionSaved}
                />
              )}

              {active === 'modo' && (
                <BotModeSection
                  mode="edit"
                  testId="tenant-section-modo"
                  tenantId={tenant.tenantId}
                  value={(cfg.bot?.mode as 'informational' | 'transactional' | 'hybrid') ?? null}
                  onSaved={onSectionSaved}
                />
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
